import React, { memo, useCallback } from 'react';
import { Package, PauseCircle, Plus, Trash2 } from 'lucide-react';
import B2BTag from '../shared/B2BTag';
import StatusSelect from './StatusSelect';
import { Button, Card, CardFooter, CardHeader, CopyButton } from './primitives';
import { itemStatusOptions } from '../shared/orderStatus';
import {
  canChangeQuantity,
  isCancelledItem,
  isLastActiveItem,
} from '../shared/orderEditRules';
import QuantityStepper from './QuantityStepper';
import {
  CURRENCY,
  formatAmount,
  formatItemCount,
} from '../shared/orderFormat';

/*
 * The order's line items — the reason this route exists, and now the first
 * thing on the page rather than the third.
 *
 * Two performance properties matter here:
 *
 * 1. Rows are memoised and their handler is bound per row, so typing in the
 *    tracking box or the refund form no longer re-renders every row. Previously
 *    all of that state lived at the top of a 1257-line component and a single
 *    keystroke re-rendered the whole table plus one react-select per row.
 *
 * 2. The status label is resolved from a Map passed in by the parent, not by a
 *    `.find()` inside the row map, which was O(rows × statuses) every render.
 */

const OrderItemRow = memo(function OrderItemRow({
  item,
  statusOptions: allStatusOptions,
  isEditable,
  canEditContents,
  isLastActive,
  isPending,
  isSaving,
  onStatusChange,
  onQuantityChange,
  onRemove,
}) {
  const handleChange = useCallback(
    (statusId) => onStatusChange(item, statusId),
    [item, onStatusChange],
  );

  // Built per row: a held item has to keep its own status in the list to have
  // anything to display, even though hold can never be chosen here.
  const statusOptions = itemStatusOptions(allStatusOptions, item.status);

  const categories = Array.isArray(item.category_title)
    ? item.category_title.join(', ')
    : item.category_title;

  // A removed line stays in the payload as Cancelled rather than disappearing,
  // so it is muted and struck through — present for the record, visibly not
  // part of what is being fulfilled or charged.
  const removed = isCancelledItem(item);

  return (
    <tr
      className={`border-b border-[#F0F4F5] last:border-b-0 ${
        removed ? 'bg-[#FCFDFD]' : ''
      }`}
    >
      <td className="px-3.5 py-2.5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg border border-[#E1E7EA] bg-[#F5F8F8] overflow-hidden shrink-0 flex items-center justify-center">
            {item.image ? (
              <img
                className="w-full h-full object-cover"
                src={item.image}
                alt=""
                width="36"
                height="36"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <Package
                className="w-3.5 h-3.5 text-[#C4CFD5]"
                aria-hidden="true"
              />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span
                className={`text-[13px] font-semibold truncate max-w-[240px] ${
                  removed
                    ? 'text-[#8A9BA8] line-through'
                    : 'text-nysmah-dark'
                }`}
              >
                {item.product_name}
              </span>
              {Number(item.is_b2b) === 1 && <B2BTag />}
            </div>
            <div className="flex items-center gap-1 text-[11px] font-medium text-[#7B8A93]">
              <span className="truncate max-w-[210px] tabular-nums">
                {categories || '—'}
                {item.barcode ? ` · ${item.barcode}` : ''}
              </span>
              {item.barcode && (
                <CopyButton value={item.barcode} label="Copy barcode" />
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5">
        <StatusSelect
          size="sm"
          value={item.status}
          options={statusOptions}
          onChange={handleChange}
          disabled={!isEditable}
          pending={isPending}
          ariaLabel={`Status for ${item.product_name || 'item'}`}
        />
      </td>
      <td className="px-3 py-2.5 text-right">
        <QuantityStepper
          value={Number(item.quantity)}
          onCommit={(next) => onQuantityChange(item, next)}
          disabled={!canEditContents || !canChangeQuantity(item)}
          isPending={isSaving}
        />
      </td>
      <td className="px-3 py-2.5 text-right text-[13px] font-medium text-[#3B4A54] tabular-nums">
        {formatAmount(item.unit_price)}
      </td>
      <td
        className={`px-3.5 py-2.5 text-right text-[13px] font-bold tabular-nums ${
          removed ? 'text-[#8A9BA8] line-through' : 'text-nysmah-dark'
        }`}
      >
        {formatAmount(item.final_price)}
      </td>
      {canEditContents && (
        <td className="px-2 py-2.5 text-right">
          {!removed && (
            <button
              type="button"
              onClick={() => onRemove(item)}
              disabled={isSaving || isLastActive}
              title={
                isLastActive
                  ? 'This is the only item left — cancel the whole order instead'
                  : `Remove ${item.product_name || 'item'}`
              }
              aria-label={`Remove ${item.product_name || 'item'}`}
              className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-transparent bg-transparent text-nysmah-muted cursor-pointer hover:text-[#C2453D] hover:bg-[#FCF2F2] disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent"
            >
              <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          )}
        </td>
      )}
    </tr>
  );
});

const OrderItemsTable = ({
  items,
  statusOptions,
  isEditable,
  canEditContents = false,
  editBlockedReason,
  canOfferHold = false,
  onHoldToEdit,
  pendingItemIds,
  isItemSaving,
  onItemStatusChange,
  onQuantityChange,
  onRemoveItem,
  onAddItem,
  itemCount,
  unitCount,
}) => (
  <Card>
    <CardHeader
      title="Items"
      action={
        <span className="text-[11.5px] font-medium text-[#6B7C86] tabular-nums">
          {formatItemCount(itemCount, unitCount)}
        </span>
      }
    />
    <div className="overflow-x-auto">
      <table className="w-full">
        <caption className="sr-only">
          Line items for this order. Amounts are in {CURRENCY}, inclusive of VAT.
        </caption>
        <thead>
          <tr className="border-b border-[#E1E7EA] bg-[#F9FBFB]">
            <th className="text-left text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#6B7C86] px-3.5 py-2">
              Item
            </th>
            <th className="text-left text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#6B7C86] px-3 py-2 min-w-[136px]">
              Status
            </th>
            <th className="text-right text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#6B7C86] px-3 py-2">
              Qty
            </th>
            <th className="text-right text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#6B7C86] px-3 py-2">
              Unit
            </th>
            <th className="text-right text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#6B7C86] px-3.5 py-2 whitespace-nowrap">
              Total ({CURRENCY})
            </th>
            {canEditContents && <th className="w-10"><span className="sr-only">Remove</span></th>}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td
                colSpan={canEditContents ? 6 : 5}
                className="text-center text-[13px] font-medium text-[#7B8A93] py-10"
              >
                No items on this order.
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <OrderItemRow
                key={item.id ?? item.product_id}
                item={item}
                statusOptions={statusOptions}
                isEditable={isEditable}
                canEditContents={canEditContents}
                isLastActive={isLastActiveItem(items, item)}
                isPending={pendingItemIds.has(item.id)}
                isSaving={Boolean(isItemSaving?.(item.id))}
                onStatusChange={onItemStatusChange}
                onQuantityChange={onQuantityChange}
                onRemove={onRemoveItem}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
    {canEditContents ? (
      <CardFooter>
        <Button size="sm" onClick={onAddItem}>
          <Plus className="w-3.5 h-3.5" aria-hidden="true" />
          Add item
        </Button>
      </CardFooter>
    ) : (
      editBlockedReason && (
        <CardFooter>
          {/* Editing requires the order to be paused first, so the blocked
              state offers the pause rather than just naming the rule. */}
          <span className="text-[11.5px] font-medium text-[#6B7C86] flex-1 min-w-0">
            {editBlockedReason}
          </span>
          {canOfferHold && (
            <Button size="sm" onClick={onHoldToEdit}>
              <PauseCircle className="w-3.5 h-3.5" aria-hidden="true" />
              Hold &amp; edit
            </Button>
          )}
        </CardFooter>
      )
    )}
  </Card>
);

export default memo(OrderItemsTable);
